import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Eye, 
  RefreshCw,
  Download,
  AlertCircle,
  Wallet,
  CreditCard,
  User,
  Calendar,
  Settings,
  Save,
  Trash2,
  HardDrive
} from 'lucide-react';
import { toast } from 'sonner';

export default function PendingPaymentsTab({ onUpdate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [storageStats, setStorageStats] = useState(null);
  const [deleteProofOrder, setDeleteProofOrder] = useState(null);
  const [deletingProof, setDeletingProof] = useState(false);
  
  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    first_year_price: 699.00,
    renewal_price: 99.00,
    include_vat: true,
    vat_percentage: 12.0,
    gcash_number: '09164543040',
    maya_number: '09164543040',
    account_name: 'Severino Guillermo'
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchSettings();
    fetchStorageStats();
  }, [statusFilter]);

  const fetchStorageStats = async () => {
    try {
      const response = await api.get('/admin/payment-proof-storage');
      setStorageStats(response.data);
    } catch (error) {
      console.log('Could not fetch storage stats');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/payment-orders?status=${statusFilter}`);
      setOrders(response.data.orders || []);
      setPendingCount(response.data.pending_count || 0);
    } catch (error) {
      toast.error('Failed to load payment orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/subscription-settings');
      setSettings(response.data);
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const handleApprove = async (orderId) => {
    setProcessing(true);
    try {
      await api.post('/admin/payment-orders/approve', {
        order_id: orderId,
        notes: 'Payment verified via admin dashboard'
      });
      toast.success('Payment approved! User subscription activated.');
      fetchOrders();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/admin/payment-orders/reject', {
        order_id: selectedOrder.order_id,
        reason: rejectionReason
      });
      toast.success('Payment rejected. User has been notified.');
      setShowRejectDialog(false);
      setRejectionReason('');
      setSelectedOrder(null);
      fetchOrders();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteProof = async () => {
    if (!deleteProofOrder) return;
    
    const fileId = deleteProofOrder.proof_of_payment_url?.split('/').pop();
    if (!fileId) {
      toast.error('Could not find file to delete');
      return;
    }

    setDeletingProof(true);
    try {
      await api.delete(`/admin/payment-proof/${fileId}`);
      toast.success('Proof file deleted successfully');
      setDeleteProofOrder(null);
      fetchOrders();
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    } finally {
      setDeletingProof(false);
    }
  };

  const viewProof = async (order) => {
    if (!order.proof_of_payment_url) return;
    
    try {
      // Remove /api prefix if present since axios base URL already includes it
      const url = order.proof_of_payment_url.replace(/^\/api/, '');
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to load proof image');
    }
  };

  const downloadProof = async (order) => {
    if (!order.proof_of_payment_url) return;
    
    try {
      // Remove /api prefix if present since axios base URL already includes it
      const url = order.proof_of_payment_url.replace(/^\/api/, '');
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `proof_${order.order_id}.${order.proof_of_payment_url.split('.').pop()}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/subscription-settings', settings);
      toast.success('Settings saved successfully!');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getMethodIcon = (method) => {
    return method === 'gcash' 
      ? <Wallet className="w-4 h-4 text-blue-500" />
      : <CreditCard className="w-4 h-4 text-green-500" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header with Stats and Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-xl font-semibold">Payment Orders</h2>
          {pendingCount > 0 && (
            <Badge className="bg-amber-500 text-white">
              {pendingCount} Pending
            </Badge>
          )}
          {storageStats && storageStats.total_files > 0 && (
            <Badge variant="outline" className="text-gray-600">
              <HardDrive className="w-3 h-3 mr-1" />
              {storageStats.total_files} files ({storageStats.total_size_mb} MB)
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchOrders(); fetchStorageStats(); }}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="w-4 h-4 mr-2" /> Pricing Settings
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Label>Filter by Status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All Orders</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No {statusFilter !== 'all' ? statusFilter : ''} payment orders found</p>
            </div>
          ) : (
            <div className="divide-y">
              {orders.map((order) => (
                <div key={order.order_id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono font-bold text-lg">#{order.order_id}</span>
                        {getStatusBadge(order.status)}
                        <div className="flex items-center gap-1 text-gray-500">
                          {getMethodIcon(order.payment_method)}
                          <span className="uppercase text-sm">{order.payment_method}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>{order.user_email}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(order.created_at)}</span>
                        </div>
                        {order.contact_number && (
                          <span>📱 {order.contact_number}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-4">
                        <span className="text-lg font-bold text-emerald-600">
                          ₱{order.total_amount?.toFixed(2) || '0.00'}
                        </span>
                        <Badge variant="secondary">
                          {order.subscription_type === 'first_year' ? 'First Year' : 'Renewal'}
                        </Badge>
                      </div>

                      {order.rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
                          <strong>Rejection Reason:</strong> {order.rejection_reason}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {order.proof_of_payment_url && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewProof(order)}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadProof(order)}
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                          {order.status !== 'pending' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteProofOrder(order)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Delete File
                            </Button>
                          )}
                        </>
                      )}

                      {order.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprove(order.order_id)}
                            disabled={processing || !order.proof_of_payment_url}
                            title="Approve payment and activate subscription immediately"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve & Activate
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowRejectDialog(true);
                            }}
                            disabled={processing}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}

                      {!order.proof_of_payment_url && order.status === 'pending' && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Awaiting Proof Upload
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this payment. The user will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Textarea
              id="reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Payment amount does not match, Invalid screenshot, Transaction not found..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={processing || !rejectionReason.trim()}
            >
              {processing ? 'Processing...' : 'Reject Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subscription Pricing Settings</DialogTitle>
            <DialogDescription>
              Configure pricing and payment account details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Year Price (₱)</Label>
                <Input
                  type="number"
                  value={settings.first_year_price}
                  onChange={(e) => setSettings({...settings, first_year_price: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Renewal Price (₱)</Label>
                <Input
                  type="number"
                  value={settings.renewal_price}
                  onChange={(e) => setSettings({...settings, renewal_price: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="include_vat"
                  checked={settings.include_vat}
                  onChange={(e) => setSettings({...settings, include_vat: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="include_vat">Include VAT</Label>
              </div>
              <div>
                <Label>VAT %</Label>
                <Input
                  type="number"
                  value={settings.vat_percentage}
                  onChange={(e) => setSettings({...settings, vat_percentage: parseFloat(e.target.value)})}
                  disabled={!settings.include_vat}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Payment Accounts</h4>
              <div>
                <Label>Account Name</Label>
                <Input
                  value={settings.account_name}
                  onChange={(e) => setSettings({...settings, account_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <Label>GCash Number</Label>
                  <Input
                    value={settings.gcash_number}
                    onChange={(e) => setSettings({...settings, gcash_number: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Maya Number</Label>
                  <Input
                    value={settings.maya_number}
                    onChange={(e) => setSettings({...settings, maya_number: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              <Save className="w-4 h-4 mr-2" />
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Proof Confirmation Dialog */}
      <Dialog open={!!deleteProofOrder} onOpenChange={(open) => !open && setDeleteProofOrder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Proof of Payment File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this proof of payment file? This will free up storage space but the file cannot be recovered.
            </DialogDescription>
          </DialogHeader>
          {deleteProofOrder && (
            <div className="bg-gray-50 rounded-lg p-4 my-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Order ID:</div>
                <div className="font-medium">#{deleteProofOrder.order_id}</div>
                <div className="text-gray-500">User:</div>
                <div className="font-medium">{deleteProofOrder.user_email}</div>
                <div className="text-gray-500">Status:</div>
                <div className="font-medium capitalize">{deleteProofOrder.status}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteProofOrder(null)} disabled={deletingProof}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteProof}
              disabled={deletingProof}
            >
              {deletingProof ? 'Deleting...' : 'Delete File'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
